import React, { useState } from "react";
import Layout from "../components/Layout";
import { useApp } from "../context/AppContext";
import {
  Package,
  AlertTriangle,
  XOctagon,
  Box,
  Plus,
  ArrowLeftRight,
  Printer,
  ClipboardList,
} from "lucide-react";
import MovementForm from "../components/MovementForm";
import StatusBadge from "../components/StatusBadge";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { generateGiacenzaPDF, generateRiordinoPDF } from "../lib/pdf";

function KpiCard({ label, value, icon: Icon, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-white border-slate-200 text-slate-900",
    ok: "bg-emerald-50 border-emerald-200 text-emerald-900",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
    danger: "bg-red-50 border-red-200 text-red-900",
  };
  return (
    <div
      className={`panel p-6 flex items-start justify-between ${tones[tone]}`}
      data-testid={testid}
    >
      <div>
        <div className="eyebrow">{label}</div>
        <div className="kpi-value text-4xl mt-2">{value}</div>
      </div>
      <div className="p-2 rounded bg-white/60 border border-current/10">
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { products, stats, lowStock } = useApp();
  const [openMov, setOpenMov] = useState(false);

  return (
    <Layout
      title="Dashboard Giacenza"
      subtitle="Panoramica magazzino reti per confezionamento molluschi"
    >
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Totale Prodotti"
          value={stats.total_products}
          icon={Package}
          testid="kpi-total-products"
        />
        <KpiCard
          label="Totale Giacenza"
          value={Math.round(stats.totale_giacenza)}
          icon={Box}
          testid="kpi-total-stock"
        />
        <KpiCard
          label="In Esaurimento"
          value={stats.in_esaurimento}
          icon={AlertTriangle}
          tone={stats.in_esaurimento > 0 ? "warn" : "neutral"}
          testid="kpi-low-stock"
        />
        <KpiCard
          label="Esauriti"
          value={stats.esauriti}
          icon={XOctagon}
          tone={stats.esauriti > 0 ? "danger" : "neutral"}
          testid="kpi-out-of-stock"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="panel p-5 flex items-start gap-4 lg:col-span-2">
          <div className="flex-1">
            <div className="eyebrow">Azione Rapida</div>
            <h3 className="text-lg font-semibold mt-1 mb-2">
              Registra un movimento
            </h3>
            <p className="text-sm text-slate-600">
              Carico dal fornitore o scarico per produzione.
            </p>
          </div>
          <Button
            onClick={() => setOpenMov(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="dashboard-open-movement"
          >
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Nuovo movimento
          </Button>
        </div>

        <div className="panel p-5">
          <div className="eyebrow">Stampe</div>
          <h3 className="text-lg font-semibold mt-1 mb-3">Esporta PDF</h3>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={() => generateGiacenzaPDF(products)}
              disabled={products.length === 0}
              data-testid="dashboard-print-giacenza"
              className="justify-start"
            >
              <Printer className="w-4 h-4 mr-2" />
              Giacenza completa
            </Button>
            <Button
              variant="outline"
              onClick={() => generateRiordinoPDF(products)}
              disabled={products.length === 0}
              data-testid="dashboard-print-riordino"
              className="justify-start"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Lista riordino
            </Button>
          </div>
        </div>
      </section>

      <section className="panel" data-testid="low-stock-panel">
        <div className="panel-header">
          <div>
            <div className="eyebrow">Notifiche Magazzino</div>
            <h2 className="text-lg font-semibold mt-1">
              Prodotti da riordinare
            </h2>
          </div>
          <Link
            to="/prodotti"
            className="text-sm font-semibold text-blue-900 hover:text-blue-950 underline underline-offset-2"
            data-testid="low-stock-see-all"
          >
            Vedi tutti i prodotti →
          </Link>
        </div>

        {lowStock.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <Package className="w-6 h-6 text-emerald-700" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">
              Tutte le scorte sono OK
            </h3>
            <p className="text-sm text-slate-500">
              Nessun prodotto sotto la soglia minima. Non servono riordini.
            </p>
            {products.length === 0 && (
              <Link
                to="/prodotti"
                className="inline-block mt-4 bg-slate-900 text-white rounded px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
                data-testid="start-create-product"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Crea il primo prodotto
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Codice</th>
                  <th>Prodotto</th>
                  <th className="text-right">Giacenza</th>
                  <th className="text-right">Soglia</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.map((p) => (
                  <tr key={p.id} data-testid={`low-stock-row-${p.code}`}>
                    <td className="mono text-slate-600">{p.code}</td>
                    <td className="font-medium text-slate-900">{p.name}</td>
                    <td className="text-right mono font-semibold">
                      {p.quantity} {p.unit}
                    </td>
                    <td className="text-right mono text-slate-500">
                      {p.min_threshold} {p.unit}
                    </td>
                    <td>
                      <StatusBadge
                        quantity={p.quantity}
                        threshold={p.min_threshold}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <MovementForm open={openMov} onOpenChange={setOpenMov} />
    </Layout>
  );
}
